import { isPlatformServer } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { TrackingFeature } from '../+state';
import { ApiTypes } from '../..';
import {
    DataLayer,
    DataLayerEntry,
    TrackingElementType,
    TrackingEvent,
    TrackingPageElementStatusType,
} from '../utilities/tracking';
import { ConsentService } from './consent.service';

declare global {
    interface Window {
        dataLayer: DataLayer;
    }
}

@Injectable({
    providedIn: 'root',
})
export class EventTrackingService {
    protected readonly TRACKING_CONSENT_GROUP_ID = 'C0002';
    protected readonly store = inject(Store);
    protected readonly platformId: object = inject(PLATFORM_ID);
    protected readonly dataLayerPushs$ = new ReplaySubject<DataLayerEntry>();
    protected readonly consentService = inject(ConsentService);

    protected logs = false;

    init(logs: boolean): void {
        this.logs = logs;
        this.dataLayerPushs$.pipe(takeUntilDestroyed()).subscribe((pushObject) => {
            this.getDataLayer().push(pushObject);
        });
    }

    protected getDataLayer(): DataLayer {
        if (isPlatformServer(this.platformId)) {
            return [];
        }
        if (!window.dataLayer) {
            window.dataLayer = [];
        }
        return window.dataLayer;
    }

    public pushDataLayer(dataLayer: DataLayerEntry): void {
        if (!this.consentService.hasConsent(this.TRACKING_CONSENT_GROUP_ID)) {
            // oxlint-disable-next-line no-console
            console.log(`📈 pushDataLayer - No consent for ${this.TRACKING_CONSENT_GROUP_ID}, skipping push`);
            return;
        }

        this.dataLayerPushs$.next(dataLayer);
        if (this.logs) {
            // oxlint-disable-next-line no-console
            console.log('📈 pushDataLayer', dataLayer);
        }
    }

    trackButton(tracking: {
        elType: TrackingElementType;
        event: TrackingEvent;
        elLabel?: string;
        elTarget?: string;
        vehicle?: ApiTypes.VehicleDetail;
        status?: TrackingPageElementStatusType;
    }): void {
        this.store.dispatch(
            TrackingFeature.actions.clickTracking({
                elType: tracking.elType,
                elLabel: tracking.elLabel,
                elTarget: tracking.elTarget,
                event: tracking.event,
                vehicle: tracking.vehicle,
                status: tracking.status,
            }),
        );
    }

    trackEngagementClick(label: string) {
        this.store.dispatch(
            TrackingFeature.actions.eventTrackingPush({
                dataLayer: {
                    event: 'engagement_click',
                    page: {
                        element: {
                            label,
                            type: 'button',
                        },
                    },
                },
            }),
        );
    }
}
